import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import buildAndCleanQueryString from "./buildAndCleanQueryString";
import cleanInitialStateFromQuery from "./cleanInitialStateFromQuery";
import transformKeys from "./transformKeys";

export interface Preferences<T extends object> {
  exclude?: (keyof T)[];
  replaceSlashes?: (keyof T)[];
  removeWhenContains?: string[]; // words to be added to a regex filter
}

export default function useSearchBarParams<T extends object>(
  preferences: Preferences<T> = {}
): {
  initialParams: T;
  setParams: (values: T) => void;
  hasQueryParams: boolean;
  clearParams: () => void;
} {
  const location = useLocation();
  const navigate = useNavigate();

  let initialParams = {} as T;

  const searchParams = useMemo((): T => {
    const params = {} as T;
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.forEach((value, key) => {
      (params as any)[key] = value;
    });
    return params;
  }, [location]);

  const hasQueryParams = !!Object.entries(searchParams).length;

  if (hasQueryParams) {
    const transformToValues = transformKeys("back", searchParams);
    initialParams = cleanInitialStateFromQuery<T>(
      transformToValues as T,
      preferences
    );
  }

  function setParams(values: T): void {
    const transformedValues = transformKeys("to", values) as T;
    const queryString = buildAndCleanQueryString<T>(
      transformedValues,
      preferences
    );
    if (navigate) {
      navigate({
        search: queryString,
      });
    }
  }
  const clearParams = () =>
    navigate({
      search: "",
    });

  return { initialParams, setParams, hasQueryParams, clearParams };
}
